import { useRef } from "react";
import { FIT_LINE_HEIGHT, FIT_TEXT_CLASS, useFitToBox } from "./useFitToBox";

/**
 * Read-only option text, scaled to fill its container.
 *
 * plan.md §2.3: both options fully visible, no scrolling and no truncation, so
 * the two sides stay comparable at a glance under a 30-second timer. Option
 * text runs from a few words to 200 characters, so a fixed size either wastes
 * the box or overflows it.
 */
export const FitText = ({
  children,
  max = 44,
  min = 14,
  className,
}: {
  children: string;
  max?: number;
  min?: number;
  className?: string;
}) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const size = useFitToBox(boxRef, textRef, children, { min, max });

  return (
    /*
     * The measured box is absolutely positioned, so it is exactly the space
     * available and cannot be grown by the text inside it. In normal flow an
     * `h-full` box expands with its content whenever an ancestor height is not
     * definite, and the fit then always "succeeds" at the largest size.
     */
    <div className={`relative h-full w-full ${className ?? ""}`}>
      <div
        ref={boxRef}
        className="absolute inset-0 flex items-center justify-center overflow-hidden"
      >
        <span
          ref={textRef}
          style={{ fontSize: size, lineHeight: FIT_LINE_HEIGHT }}
          className={FIT_TEXT_CLASS}
        >
          {children}
        </span>
      </div>
    </div>
  );
};
